import { ComponentPropsWithoutRef, ForwardedRef, ReactNode, forwardRef } from 'react';
import * as Avatar from '@givebutter/primitives/avatar';
import { faArrowProgress } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCrmMembers } from 'components/tasks/utils/useCrmMembers';
import { getAvatarFallbackColor } from 'components/util/getAvatarFallbackColor';
import { dateFromServer } from 'helpers/dates';
import { getInitialsFromName } from 'helpers/formatting';
import { DateTime } from 'luxon';
import { twMerge } from 'tailwind-merge';
import { CrmMember, TaskHistoryFieldUpdate, TaskHistoryItem } from 'types/domain';

interface HistoryItemRootProps extends ComponentPropsWithoutRef<'div'> {
    user: TaskHistoryItem['user'];
}
const HistoryItem = forwardRef(
    (
        { user, children, className, ...restProps }: HistoryItemRootProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        return (
            <div
                className={twMerge('relative flex min-h-10 flex-col text-sm', className)}
                ref={forwardedRef}
                {...restProps}
            >
                <div className="flex items-start gap-2">
                    <div className="size-5">
                        <Avatar.Root size="2xs" className="align-top">
                            {user ? (
                                <Avatar.Fallback
                                    className={getAvatarFallbackColor(user.first_name)}
                                    aria-label={`Avatar for ${user.first_name} ${user.last_name}`}
                                >
                                    {getInitialsFromName(user.first_name, user.last_name)}
                                </Avatar.Fallback>
                            ) : (
                                <Avatar.Fallback
                                    aria-label="Progress icon"
                                    className="bg-gray-100 text-gray-500"
                                >
                                    <FontAwesomeIcon icon={faArrowProgress} />
                                </Avatar.Fallback>
                            )}
                        </Avatar.Root>
                    </div>
                    <div className="pb-4">{children}</div>
                </div>
                <div className="absolute bottom-0.5 left-2.5 top-5 border-l border-gray-200 group-last:hidden" />
            </div>
        );
    },
);

const HistoryItemPrimaryText = ({ children }: { children: ReactNode }) => {
    return <strong className="text-gray-900">{children} </strong>;
};

const HistoryItemSecondaryText = ({ children }: { children: ReactNode }) => {
    return <span className="text-gray-600">{children}</span>;
};

interface HistoryItemAssignmentTextProps {
    newValue: string | number | null;
    oldValue: string | number | null;
    user?: CrmMember;
}
const HistoryItemAssignmentText = ({
    newValue,
    oldValue,
    user,
}: HistoryItemAssignmentTextProps) => {
    return (
        <HistoryItemSecondaryText>
            {newValue && user ? (
                <>
                    {oldValue ? 're-assigned' : 'assigned'} to{' '}
                    <HistoryItemPrimaryText>
                        {user.first_name} {user.last_name}
                    </HistoryItemPrimaryText>
                </>
            ) : (
                'removed assignee'
            )}
        </HistoryItemSecondaryText>
    );
};

const HistoryItemUpdatedEvent = ({ item }: { item: TaskHistoryItem }) => {
    const userName = `${item.user?.first_name} ${item.user?.last_name}`;
    const oldValues = item.old_values as TaskHistoryFieldUpdate;
    const newValues = item.new_values as TaskHistoryFieldUpdate;
    const { data: members } = useCrmMembers({ limit: 9999 });

    switch (item.field) {
        case 'status': {
            return (
                <HistoryItem user={item.user}>
                    <HistoryItemPrimaryText>{userName}</HistoryItemPrimaryText>{' '}
                    <HistoryItemSecondaryText>
                        marked {getStatusText(newValues['status']?.toString() ?? '')} on{' '}
                        {dateFromServer(item.created_at).toLocaleString(DateTime.DATE_FULL)}
                    </HistoryItemSecondaryText>
                </HistoryItem>
            );
        }
        case 'assigned_to': {
            const user = members.find(m => m.id === newValues['assigned_to']);
            return (
                <HistoryItem user={item.user}>
                    <HistoryItemPrimaryText>{userName}</HistoryItemPrimaryText>{' '}
                    <HistoryItemAssignmentText
                        newValue={newValues['assigned_to']}
                        oldValue={oldValues['assigned_to']}
                        user={user}
                    />
                </HistoryItem>
            );
        }
        case 'due_at':
            return (
                <HistoryItem user={item.user}>
                    <HistoryItemPrimaryText>{userName}</HistoryItemPrimaryText>{' '}
                    <HistoryItemSecondaryText>
                        {newValues['due_at'] ? (
                            <>
                                changed due date to{' '}
                                <HistoryItemPrimaryText>
                                    {dateFromServer(newValues['due_at'].toString()).toLocaleString(
                                        DateTime.DATE_FULL,
                                    )}
                                </HistoryItemPrimaryText>
                            </>
                        ) : (
                            'removed the due date'
                        )}
                    </HistoryItemSecondaryText>
                </HistoryItem>
            );
        case 'title':
            return (
                <HistoryItem user={item.user}>
                    <HistoryItemPrimaryText>{userName}</HistoryItemPrimaryText>{' '}
                    <HistoryItemSecondaryText>
                        changed title from {oldValues['title']} to {newValues['title']}
                    </HistoryItemSecondaryText>
                </HistoryItem>
            );
        case 'description':
            return (
                <HistoryItem user={item.user}>
                    <HistoryItemPrimaryText>{userName}</HistoryItemPrimaryText>{' '}
                    <HistoryItemSecondaryText>updated the description</HistoryItemSecondaryText>
                </HistoryItem>
            );
        default:
            return null;
    }
};

const getStatusText = (status: string) => {
    switch (status) {
        case 'todo':
            return 'to do';
        case 'in_progress':
            return 'in progress';
        case 'complete':
            return 'complete';
        default:
            return '';
    }
};

const Root = HistoryItem;
const PrimaryText = HistoryItemPrimaryText;
const SecondaryText = HistoryItemSecondaryText;
const AssignmentText = HistoryItemAssignmentText;
const UpdatedEvent = HistoryItemUpdatedEvent;

export {
    HistoryItem,
    HistoryItemPrimaryText,
    HistoryItemSecondaryText,
    HistoryItemAssignmentText,
    HistoryItemUpdatedEvent,

    //
    Root,
    PrimaryText,
    SecondaryText,
    AssignmentText,
    UpdatedEvent,
};
